// Per-user storage: routes all localStorage.* calls through the signed-in user namespace.
import { scopedStorage as localStorage } from "../services/userStorage";
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
    BookOpen,
    Check,
    CheckCircle2,
    Heart,
    Mail,
    Save,
    Sparkles,
    Type
} from 'lucide-react';
import ScreenLayout from './ScreenLayout';
import { getDailyPrayer } from '../services/dailyContentService';
import { getBibleReaderPath } from '../services/bibleReaderService';
import {
    addEntry as addJournalEntry,
    getAllEntries,
    initializeJournal
} from '../services/journalService';

const formatToday = (date) => new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
}).format(new Date(`${date}T12:00:00`));

const DailyPrayer = () => {
    const [todaysPrayer] = useState(() => getDailyPrayer());
    const completionKey = `prayer_app_daily_prayer_${todaysPrayer.date}`;
    const [journalContent, setJournalContent] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [recentEntries, setRecentEntries] = useState([]);
    const [largeText, setLargeText] = useState(false);
    const [hasPrayed, setHasPrayed] = useState(() => localStorage.getItem(completionKey) === 'complete');

    useEffect(() => {
        initializeJournal();
        setRecentEntries(getAllEntries().slice(0, 3));
    }, []);

    const saveReflection = () => {
        if (!journalContent.trim()) return;

        setIsSaving(true);
        try {
            addJournalEntry({
                title: `Reflection on: ${todaysPrayer.title}`,
                content: journalContent,
                tags: ['daily prayer', 'reflection'],
                relatedScripture: todaysPrayer.scriptureReference
            });
            setJournalContent('');
            setSaveSuccess(true);
            setRecentEntries(getAllEntries().slice(0, 3));
            window.setTimeout(() => setSaveSuccess(false), 3000);
        } catch (error) {
            console.error('Error saving journal entry:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const togglePrayerComplete = () => {
        const nextValue = !hasPrayed;
        setHasPrayed(nextValue);
        if (nextValue) {
            localStorage.setItem(completionKey, 'complete');
        } else {
            localStorage.removeItem(completionKey);
        }
    };

    const reflectionState = {
        reflection: {
            title: `Reflection on: ${todaysPrayer.title}`,
            relatedScripture: todaysPrayer.scriptureReference,
            sourceLabel: 'Daily prayer',
            sourcePath: '/prayer'
        }
    };

    return (
        <ScreenLayout
            title="Daily Prayer"
            className={`detail-prayer${largeText ? ' detail-large-text' : ''}`}
            actions={(
                <button
                    type="button"
                    className="module-text-control"
                    onClick={() => setLargeText(!largeText)}
                    aria-label={largeText ? 'Use standard text size' : 'Use larger text size'}
                    aria-pressed={largeText}
                >
                    <Type size={18} /> Text
                </button>
            )}
        >
            <header className="detail-heading">
                <p className="detail-date">{formatToday(todaysPrayer.date)}</p>
                <h1>{todaysPrayer.title}</h1>
                <p className="detail-subtitle">{todaysPrayer.scriptureReference}</p>
                <p className="detail-subtitle">Come as you are. Begin with one quiet breath.</p>
            </header>

            <div className="detail-grid">
                <article className="detail-paper">
                    <section id="daily-prayer-read" className="detail-section" aria-labelledby="scripture-heading">
                        <h2 id="scripture-heading" className="detail-section-title">Scripture</h2>
                        <div className="detail-scripture">
                            <blockquote>“{todaysPrayer.scripture}”</blockquote>
                            <cite>{todaysPrayer.scriptureReference}</cite>
                        </div>
                        <div className="detail-actions">
                            <Link className="detail-button detail-button-secondary" to={getBibleReaderPath(todaysPrayer.scriptureReference)}>
                                <BookOpen size={18} /> Read the full chapter
                            </Link>
                        </div>
                    </section>

                    <section id="daily-prayer-pray" className="detail-section" aria-labelledby="prayer-heading">
                        <h2 id="prayer-heading" className="detail-section-title">Today’s prayer</h2>
                        <p>{todaysPrayer.prayer}</p>
                    </section>

                    <section id="daily-prayer-reflect" className="detail-section" aria-labelledby="reflection-heading">
                        <h2 id="reflection-heading" className="detail-section-title">Reflection</h2>
                        <p>{todaysPrayer.reflection}</p>
                        <div className="detail-actions">
                            <Link className="detail-button detail-button-secondary" to="/practice/journal" state={reflectionState}>
                                Reflect in journal
                            </Link>
                        </div>
                    </section>

                    <section id="daily-prayer-note" className="detail-section" aria-labelledby="journal-heading">
                        <h2 id="journal-heading" className="detail-section-title">A note from today</h2>
                        <form className="detail-form" onSubmit={(event) => { event.preventDefault(); saveReflection(); }}>
                            <div className="detail-field">
                                <label htmlFor="daily-prayer-reflection">What is on your heart?</label>
                                <textarea
                                    id="daily-prayer-reflection"
                                    rows="5"
                                    placeholder="Write a prayer, a name, or one thought you want to remember..."
                                    value={journalContent}
                                    onChange={(event) => setJournalContent(event.target.value)}
                                />
                            </div>
                            <div className="detail-actions">
                                <button className="detail-button" type="submit" disabled={isSaving || !journalContent.trim()}>
                                    <Save size={18} /> {isSaving ? 'Saving…' : 'Save reflection'}
                                </button>
                                <span className="detail-status" role="status">
                                    {saveSuccess && <><Check size={17} /> Saved to your journal</>}
                                </span>
                            </div>
                        </form>
                    </section>
                </article>

                <aside className="detail-rail">
                    <nav className="detail-rail-section detail-contents" aria-label="Prayer sections">
                        <h2>This prayer</h2>
                        <a href="#daily-prayer-read">Scripture</a>
                        <a href="#daily-prayer-pray">Prayer</a>
                        <a href="#daily-prayer-reflect">Reflection</a>
                        <a href="#daily-prayer-note">Your note</a>
                    </nav>

                    <section className="detail-rail-section">
                        <h2>Take your time</h2>
                        <p>Be still. Read slowly. Speak honestly. God welcomes your own words.</p>
                        <button className="detail-button" type="button" onClick={togglePrayerComplete} aria-pressed={hasPrayed}>
                            {hasPrayed ? <CheckCircle2 size={18} /> : <Heart size={18} />}
                            {hasPrayed ? 'Prayed today' : 'I have prayed'}
                        </button>
                    </section>

                    <section className="detail-rail-section detail-contents">
                        <h2>Make it personal</h2>
                        <Link to="/prayer/generate"><Sparkles size={16} /> Create a personal prayer</Link>
                        <Link to="/schedule?type=prayer"><Mail size={16} /> Optional verse delivery</Link>
                    </section>

                    <section className="detail-rail-section detail-contents">
                        <h2>Recent notes</h2>
                        {recentEntries.length > 0 ? recentEntries.map((entry) => (
                            <Link to="/practice/journal" key={entry.id}>
                                <span>{entry.title}</span>
                                <small>{new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</small>
                            </Link>
                        )) : (
                            <p>Your saved reflections will appear here.</p>
                        )}
                    </section>
                </aside>
            </div>
        </ScreenLayout>
    );
};

export default DailyPrayer;
